import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { CheckCircle, Edit, MessageCircle, Plus, Printer, Search, Trash2, Users } from 'lucide-react';
import AgencyFilter from './AgencyFilter';

// Sanitize a phone number to E.164 (digits only). Defaults missing country
// codes to Algeria (+213) so wa.me works out of the box.
const normalizePhoneForWa = (raw) => {
  if (!raw) return '';
  let s = String(raw).replace(/[^0-9+]/g, '');
  if (s.startsWith('+')) s = s.slice(1);
  if (!s) return '';
  if (s.startsWith('00')) s = s.slice(2);
  else if (s.startsWith('0')) s = '213' + s.slice(1);
  return s;
};

// Build the wa.me URL for a client (with a friendly default greeting).
const buildWaUrl = (client) => {
  const phone = normalizePhoneForWa(client?.phone);
  if (!phone) return null;
  const message = `السلام عليكم ${client?.name || ''}،\nنتواصل معكم من وكالة صنهاجة للسفر.`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};

// Enhanced Clients Management Component
const ClientsManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'table'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cin_passport: ''
  });
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [clientFormData, setClientFormData] = useState({
    name: '',
    phone: '',
    email: '',
    cin_passport: '',
    address: ''
  });

  const fetchClients = async () => {
    try {
      const params = selectedAgency ? `?agency_id=${selectedAgency}` : '';
      const response = await axios.get(`${API}/clients${params}`);
      // Sort clients by creation date - newest first
      const sortedClients = response.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setClients(sortedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [selectedAgency]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/clients/${editingClient.id}`, formData);
      } else {
        await axios.post(`${API}/clients`, formData);
      }
      setFormData({ name: '', phone: '', cin_passport: '' });
      setEditingClient(null);
      setIsDialogOpen(false);
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      phone: client.phone,
      cin_passport: client.cin_passport
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (clientId) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        await axios.delete(`${API}/clients/${clientId}`);
        fetchClients();
      } catch (error) {
        console.error('Error deleting client:', error);
      }
    }
  };

  const handleAddClient = async () => {
    try {
      if (!clientFormData.name || !clientFormData.phone) {
        alert('يرجى إدخال الاسم ورقم الهاتف على الأقل');
        return;
      }
      
      await axios.post(`${API}/clients`, clientFormData);
      setClientFormData({
        name: '',
        phone: '',
        email: '',
        cin_passport: '',
        address: ''
      });
      setShowAddClientDialog(false);
      fetchClients();
      alert('✅ تم إضافة العميل بنجاح');
    } catch (error) {
      console.error('Error adding client:', error);
      alert('❌ حدث خطأ في إضافة العميل');
    }
  };

  const deleteClient = async (clientId) => {
    if (!confirm('هل أنت متأكد من حذف هذا العميل؟ هذا الإجراء لا يمكن التراجع عنه.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('✅ تم حذف العميل بنجاح');
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لحذف العملاء');
      } else if (error.response?.status === 400) {
        alert('❌ لا يمكن حذف هذا العميل لأنه مرتبط بعمليات موجودة');
      } else {
        alert('❌ فشل في حذف العميل: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handlePrintClients = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('❌ لم يتم فتح نافذة الطباعة. تأكد من السماح للنوافذ المنبثقة.');
      return;
    }
    const rows = filteredClients.map((client, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${client.name || '-'}</td>
        <td>${client.phone || '-'}</td>
        <td>${client.email || '-'}</td>
        <td>${client.cin_passport || '-'}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html dir="rtl">
        <head>
          <title>قائمة العملاء</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap');
            * { font-family: 'Tajawal', Arial, sans-serif; }
            body { padding: 20px; direction: rtl; }
            h2 { text-align: center; margin-bottom: 5px; }
            .info { text-align: center; color: #666; margin-bottom: 15px; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; font-size: 13px; }
            th { background: #1e293b; color: white; padding: 8px 6px; text-align: right; }
            td { border: 1px solid #ddd; padding: 6px; text-align: right; }
            tr:nth-child(even) { background: #f9fafb; }
            .footer { text-align: center; margin-top: 15px; font-size: 11px; color: #999; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h2>قائمة العملاء</h2>
          <div class="info">تاريخ الطباعة: ${new Date().toLocaleDateString('ar-DZ')} — إجمالي العملاء: ${filteredClients.length}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>اسم العميل</th>
                <th>الهاتف</th>
                <th>البريد الإلكتروني</th>
                <th>رقم الهوية / جواز السفر</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
          <div class="footer">وكالات صنهاجة للسفر</div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone.includes(searchTerm) ||
    client.cin_passport.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Agency Filter */}
      {['super_admin', 'general_accountant'].includes(user?.role) && (
        <Card className="bg-brand-50 border-brand-200">
          <CardContent className="p-4">
            <AgencyFilter 
              selectedAgency={selectedAgency}
              onAgencyChange={setSelectedAgency}
              showAllOption={true}
            />
          </CardContent>
        </Card>
      )}

      {/* Header Section */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t('clients')}</h2>
            <p className="text-gray-600 mt-1">
              إدارة وتنظيم بيانات العملاء
              {['super_admin', 'general_accountant'].includes(user?.role) && selectedAgency && (
                <span className="text-brand-600"> - الوكالة المحددة</span>
              )}
              {['super_admin', 'general_accountant'].includes(user?.role) && !selectedAgency && (
                <span className="text-green-600"> - جميع الوكالات</span>
              )}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Print Button */}
            <Button 
              onClick={handlePrintClients}
              variant="outline" 
              className="bg-green-600 hover:bg-green-700 text-white border-green-600"
            >
              🖨️ طباعة القائمة
            </Button>
            
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm text-brand-600' : 'text-gray-600'
                }`}
              >
                🔳 شبكة
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  viewMode === 'table' ? 'bg-white shadow-sm text-brand-600' : 'text-gray-600'
                }`}
              >
                📋 جدول
              </button>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-brand-600 hover:bg-brand-700">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('addClient')}
                </Button>
              </DialogTrigger>
              
            {/* Enhanced Add Client Button */}
            <Button 
              onClick={() => setShowAddClientDialog(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              ➕ إضافة عميل متقدم
            </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingClient ? '✏️ تعديل بيانات العميل' : '➕ إضافة عميل جديد'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingClient ? 'قم بتعديل بيانات العميل أدناه' : 'أدخل بيانات العميل الجديد'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">👤 {t('name')}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="أدخل الاسم الكامل"
                      required
                      className="text-right"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">📞 {t('phone')}</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="مثال: 0555123456"
                      required
                      className="text-right"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cin_passport">🆔 {t('cinPassport')}</Label>
                    <Input
                      id="cin_passport"
                      value={formData.cin_passport}
                      onChange={(e) => setFormData({ ...formData, cin_passport: e.target.value })}
                      placeholder="رقم الهوية أو جواز السفر"
                      required
                      className="text-right"
                    />
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button type="submit" className="bg-brand-600 hover:bg-brand-700">
                      {editingClient ? '💾 تحديث البيانات' : '➕ إضافة العميل'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-r from-brand-50 to-brand-50 border-brand-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-brand-800">إجمالي العملاء</p>
                <p className="text-2xl font-bold text-brand-900">{clients.length}</p>
              </div>
              <Users className="h-8 w-8 text-brand-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-green-800">العملاء النشطون</p>
                <p className="text-2xl font-bold text-green-900">{Math.floor(clients.length * 0.8)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-right">
                <p className="text-sm font-medium text-purple-800">عملاء جدد هذا الشهر</p>
                <p className="text-2xl font-bold text-purple-900">{Math.floor(clients.length * 0.2)}</p>
              </div>
              <Plus className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="🔍 البحث في العملاء (الاسم، الهاتف، رقم الهوية)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-right"
              />
            </div>
            <Button variant="outline" size="sm" onClick={handlePrintClients} data-testid="print-clients-btn">
              <Printer className="h-4 w-4 ml-2" /> طباعة القائمة
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Clients Display */}
      {viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-4 text-lg font-medium text-gray-900">لا توجد عملاء</p>
              <p className="text-gray-600">ابدأ بإضافة عميل جديد</p>
            </div>
          ) : (
            filteredClients.map((client) => (
              <Card key={client.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center">
                      <span className="text-brand-600 font-semibold text-lg">
                        {client.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(client)}
                        className="p-2 text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                        title="تعديل"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {/* WhatsApp button - pure wa.me link (no JS redirect) */}
                      {buildWaUrl(client) ? (
                        <a
                          href={buildWaUrl(client)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors inline-flex items-center justify-center"
                          title={`تواصل مع ${client.name} عبر واتساب`}
                          data-testid={`wa-client-grid-${client.id}`}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      ) : (
                        <span
                          className="p-2 text-gray-300 rounded-lg inline-flex items-center justify-center cursor-not-allowed"
                          title="لا يوجد رقم هاتف"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </span>
                      )}
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-right space-y-2">
                    <h3 className="font-semibold text-gray-900">{client.name}</h3>
                    <p className="text-sm text-gray-600 flex items-center justify-end">
                      <span>{client.phone}</span>
                      <span className="mr-2">📞</span>
                    </p>
                    <p className="text-sm text-gray-600 flex items-center justify-end">
                      <span>{client.cin_passport}</span>
                      <span className="mr-2">🆔</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      ) : (
        // Table View
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">👤 {t('name')}</TableHead>
                  <TableHead className="text-right">📞 {t('phone')}</TableHead>
                  <TableHead className="text-right">🆔 {t('cinPassport')}</TableHead>
                  <TableHead className="text-right">⚙️ {t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      <div className="flex flex-col items-center">
                        <Users className="h-12 w-12 text-gray-400 mb-4" />
                        <p className="text-lg font-medium text-gray-900">لا توجد عملاء</p>
                        <p className="text-gray-600">ابدأ بإضافة عميل جديد</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredClients.map((client) => (
                    <TableRow key={client.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium text-right">{client.name}</TableCell>
                      <TableCell className="text-right">{client.phone}</TableCell>
                      <TableCell className="text-right">{client.cin_passport}</TableCell>
                      <TableCell>
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(client)}
                            className="text-brand-600 hover:text-brand-700"
                            title="تعديل"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {/* WhatsApp action - pure wa.me link */}
                          {buildWaUrl(client) ? (
                            <a
                              href={buildWaUrl(client)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-8 px-2 rounded-md text-green-600 hover:text-green-700 hover:bg-green-50 transition-colors"
                              title={`تواصل مع ${client.name} عبر واتساب`}
                              data-testid={`wa-client-row-${client.id}`}
                            >
                              <MessageCircle className="h-4 w-4" />
                            </a>
                          ) : (
                            <span
                              className="inline-flex items-center justify-center h-8 px-2 rounded-md text-gray-300 cursor-not-allowed"
                              title="لا يوجد رقم هاتف"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600 hover:text-red-700"
                            title="حذف"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>إضافة عميل جديد</DialogTitle>
            <DialogDescription>
              أدخل بيانات العميل الجديد للوكالة
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>الاسم الكامل *</Label>
                <Input
                  value={clientFormData.name}
                  onChange={(e) => setClientFormData({...clientFormData, name: e.target.value})}
                  placeholder="اسم العميل الكامل"
                />
              </div>
              <div>
                <Label>رقم الهاتف *</Label>
                <Input
                  value={clientFormData.phone}
                  onChange={(e) => setClientFormData({...clientFormData, phone: e.target.value})}
                  placeholder="رقم الهاتف"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>البريد الإلكتروني</Label>
                <Input
                  type="email"
                  value={clientFormData.email}
                  onChange={(e) => setClientFormData({...clientFormData, email: e.target.value})}
                  placeholder="البريد الإلكتروني (اختياري)"
                />
              </div>
              <div>
                <Label>رقم الهوية/جواز السفر</Label>
                <Input
                  value={clientFormData.cin_passport}
                  onChange={(e) => setClientFormData({...clientFormData, cin_passport: e.target.value})}
                  placeholder="رقم الهوية أو جواز السفر (اختياري)"
                />
              </div>
            </div>

            <div>
              <Label>العنوان</Label>
              <Input
                value={clientFormData.address}
                onChange={(e) => setClientFormData({...clientFormData, address: e.target.value})}
                placeholder="عنوان العميل (اختياري)"
              />
            </div>

            <div className="flex justify-end space-x-2 rtl:space-x-reverse">
              <Button variant="outline" onClick={() => setShowAddClientDialog(false)}>
                إلغاء
              </Button>
              <Button 
                onClick={handleAddClient}
                disabled={!clientFormData.name || !clientFormData.phone}
                className="bg-green-600 hover:bg-green-700"
              >
                ✅ إضافة العميل
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsManagement;
